import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, Loader2, Download, XCircle } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { RESOURCE } from "@/constants/testIds";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function ResourceSuccess() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("polling");
  const [downloadUrl, setDownloadUrl] = useState(null);
  const [resourceId, setResourceId] = useState(null);
  const attempts = useRef(0);

  useEffect(() => {
    if (!sessionId) {
      setStatus("failed");
      return;
    }
    const MAX = 8;
    const INTERVAL = 2000;
    let cancelled = false;
    const poll = async () => {
      if (cancelled) return;
      try {
        const res = await axios.get(`${API}/resources/status/${sessionId}`);
        const ps = res.data?.payment_status;
        if (res.data?.resource_id) setResourceId(res.data.resource_id);
        if (ps === "paid" && res.data?.download_token) {
          setDownloadUrl(`${API}/resources/download/${res.data.download_token}`);
          setStatus("paid");
          return;
        }
        if (ps === "failed" || ps === "expired") {
          setStatus(ps);
          return;
        }
      } catch (e) {
        // continue
      }
      attempts.current += 1;
      if (attempts.current >= MAX) {
        setStatus("timeout");
        return;
      }
      setTimeout(poll, INTERVAL);
    };
    poll();
    return () => {
      cancelled = true;
    };
  }, [sessionId]);

  return (
    <div className="min-h-screen bg-[#fbfcfe]">
      <Navbar />
      <section className="pt-32 pb-20">
        <div
          data-testid={RESOURCE.successContainer}
          className="max-w-2xl mx-auto px-6 md:px-12 text-center"
        >
          {status === "polling" && (
            <div>
              <Loader2 className="w-12 h-12 mx-auto animate-spin text-[#3d6fa8]" />
              <h1 className="mt-8 font-heading text-3xl md:text-4xl font-semibold text-[#2a3b5c]">
                Preparing your download…
              </h1>
              <p className="mt-4 text-[#5c6b85]">This usually takes a few seconds.</p>
            </div>
          )}

          {status === "paid" && downloadUrl && (
            <div>
              <div className="w-20 h-20 mx-auto rounded-full bg-[#ffe89a]/50 flex items-center justify-center">
                <CheckCircle2 className="w-10 h-10 text-[#2a3b5c]" />
              </div>
              <h1 className="mt-8 font-heading text-3xl md:text-5xl font-semibold text-[#2a3b5c]">
                Thank you, your resource is ready.
              </h1>
              <p className="mt-5 text-[#5c6b85] text-lg leading-relaxed">
                A receipt is on its way to your email. The download link below
                works for 72 hours, up to 5 downloads.
              </p>
              <a
                href={downloadUrl}
                data-testid={RESOURCE.downloadButton}
                className="mt-10 inline-flex items-center gap-2 rounded-full px-7 py-4 bg-[#8ab8e8] hover:bg-[#6fa1d6] text-[#2a3b5c] font-medium transition-colors"
              >
                <Download className="w-5 h-5" />
                Download your file
              </a>
              <div className="mt-6">
                <button
                  onClick={() => navigate("/")}
                  className="text-sm text-[#5c6b85] hover:text-[#3d6fa8]"
                >
                  Back to home
                </button>
              </div>
            </div>
          )}

          {(status === "failed" || status === "expired" || status === "timeout") && (
            <div>
              <div className="w-20 h-20 mx-auto rounded-full bg-[#8ab8e8]/20 flex items-center justify-center">
                <XCircle className="w-10 h-10 text-[#3d6fa8]" />
              </div>
              <h1 className="mt-8 font-heading text-3xl md:text-4xl font-semibold text-[#2a3b5c]">
                {status === "timeout"
                  ? "Still processing…"
                  : "Payment didn’t go through"}
              </h1>
              <p className="mt-5 text-[#5c6b85]">
                {status === "timeout"
                  ? "Your payment is taking longer than expected. Please check your email for a receipt, or contact us."
                  : "No worries, try again or get in touch."}
              </p>
              <Button
                onClick={() => navigate("/#resources")}
                className="mt-10 rounded-full px-6 py-6 bg-[#8ab8e8] hover:bg-[#6fa1d6] text-[#2a3b5c]"
              >
                Back to resources
              </Button>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
